// src/pages/Events.jsx
import React, { useState, useEffect } from 'react';
import EventFilters from '../components/events/EventFilters';
import EventsList from '../components/events/EventsList';
import { eventsService } from '../services/events';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Single state object for all filters - THIS IS THE KEY FIX!
  const [filters, setFilters] = useState({});
  const [edgeNodes, setEdgeNodes] = useState([]);

  // Fetch events whenever filters change
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Build clean filters object (only include non-empty values)
        const cleanFilters = {};
        if (filters.severity) cleanFilters.severity = filters.severity;
        if (filters.celerway_edge_node_id) cleanFilters.celerway_edge_node_id = filters.celerway_edge_node_id;
        if (filters.edge_node_id) cleanFilters.edge_node_id = filters.edge_node_id; // Also send alternative name
        if (filters.start_time) cleanFilters.start_time = filters.start_time;
        if (filters.end_time) cleanFilters.end_time = filters.end_time;

        console.log('🔍 Events.jsx: Fetching events with filters:', cleanFilters);
        const data = await eventsService.getEvents(cleanFilters);
        console.log('✅ Events.jsx: Received', data?.length || 0, 'events');
        setEvents(data || []);
      } catch (err) {
        console.error('❌ Events.jsx: Error fetching events:', err);
        setError(err.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [filters]); // Re-fetch when filters change

  // Filter change handler - Simply update the filters state
  const handleFilterChange = (newFilters) => {
    console.log('🔄 Events.jsx: Filters changed to:', newFilters);
    setFilters(newFilters);
  };

  // Helper to check if any filters are active
  const hasActiveFilters = filters.severity || filters.celerway_edge_node_id || filters.start_time || filters.end_time;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Security Events</h1>
        <p className="text-gray-600 mt-2">
          Monitor and manage security events across your OT infrastructure
        </p>
      </div>

      <EventFilters 
        filters={filters}
        onFilterChange={handleFilterChange}
        onEdgeNodesLoaded={setEdgeNodes}
      />

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading events...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading events</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No events found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {hasActiveFilters 
              ? "No events match your current filter criteria. Try adjusting the filters above."
              : "No security events have been detected yet."}
          </p>
          {hasActiveFilters && (
            <button
              onClick={() => setFilters({})}
              className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear all filters
            </button>
          )}
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Found <span className="font-semibold text-gray-900">{events.length}</span> event{events.length !== 1 ? 's' : ''}
              {hasActiveFilters && (
                <span> matching your filters</span>
              )}
            </p>
            {hasActiveFilters && (
              <button
                onClick={() => setFilters({})}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
          <EventsList events={events} edgeNodes={edgeNodes} />
        </>
      )}
    </div>
  );
};

export default Events;
